import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../utils/api';
import './EmailVerification.css';

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const [jobTitle, setJobTitle] = useState('');
  
  // Resend verification state
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendEmailCooldown, setResendEmailCooldown] = useState(0);
  const [expiredTokenData, setExpiredTokenData] = useState(null); // { applicationId, email, jobId }
  
  // Phone verification state (optional, non-blocking)
  const [requiresPhoneVerification, setRequiresPhoneVerification] = useState(false);
  const [phoneVerificationStatus, setPhoneVerificationStatus] = useState('pending'); // pending, verifying, success, error
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [resendingCode, setResendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneVerificationError, setPhoneVerificationError] = useState('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const appId = searchParams.get('applicationId');
    const phone = searchParams.get('phone');
    const needsPhone = searchParams.get('needsPhone') === 'true';

    // Set up phone verification if needed (optional, non-blocking)
    // Phone verification form will be shown after email verification
    if (appId) setApplicationId(appId);
    if (phone) setPhoneNumber(phone);
    if (needsPhone) setRequiresPhoneVerification(true);

    // If token is provided, verify email (auto-verification)
    // After email verification, phone verification form will be shown if needed
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('Invalid verification link. Please click the verification link from your email.');
    }
  }, [location]);

  // Countdown timer for resend cooldown (phone)
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Countdown timer for resend email cooldown
  useEffect(() => {
    if (resendEmailCooldown > 0) {
      const timer = setTimeout(() => setResendEmailCooldown(resendEmailCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendEmailCooldown]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch('/api/jobs/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, applicationId: new URLSearchParams(window.location.search).get('applicationId') })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message);
        setJobTitle(data.jobTitle || '');
        
        // Check for phone verification from URL params or sessionStorage
        const searchParams = new URLSearchParams(location.search);
        const urlAppId = searchParams.get('applicationId');
        const urlNeedsPhone = searchParams.get('needsPhone') === 'true';
        const urlPhone = searchParams.get('phone');

        // Check sessionStorage as fallback
        const storedAppData = sessionStorage.getItem('applicationData');
        let appData = null;
        if (storedAppData) {
          try {
            appData = JSON.parse(storedAppData);
          } catch (e) {
            // Ignore parse errors
          }
        }

        // Prioritize URL params, fallback to sessionStorage
        const finalAppId = urlAppId || appData?.applicationId || '';
        const finalNeedsPhone = urlNeedsPhone || appData?.requiresPhoneVerification || false;
        const finalPhone = urlPhone || appData?.phoneNumber || '';

        // Set up phone verification if needed (optional, non-blocking)
        if (finalNeedsPhone && finalAppId && finalPhone) {
          setRequiresPhoneVerification(true);
          setApplicationId(finalAppId);
          setPhoneNumber(finalPhone);
          // Clean up sessionStorage after using it
          if (appData) {
            sessionStorage.removeItem('applicationData');
          }
        }
      } else {
        setStatus('error');
        setMessage(data.error || 'Verification failed');
        
        // Store expired token data if available for resend functionality
        if (data.expired && (data.applicationId || data.email || data.jobId)) {
          setExpiredTokenData({
            applicationId: data.applicationId,
            email: data.email,
            jobId: data.jobId
          });
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('Failed to verify email. Please try again.');
    }
  };

  const verifyPhone = async (codeOverride = null) => {
    const codeToVerify = codeOverride || phoneCode;
    if (!applicationId || !codeToVerify || codeToVerify.length !== 6) {
      setPhoneVerificationStatus('error');
      return;
    }

    setPhoneVerificationStatus('verifying');
    setPhoneVerificationError('');
    try {
      const response = await jobsAPI.verifyPhone(applicationId, codeToVerify);
      if (response.data.success) {
        setPhoneVerificationStatus('success');
        setPhoneCode('');
      }
    } catch (error) {
      setPhoneVerificationStatus('error');
      setPhoneCode(''); // Clear code on error so user can retry
      const errMsg = error.response?.data?.error || error.message || 'Invalid code. Please try again.';
      setPhoneVerificationError(errMsg);
      console.error('Phone verification error:', errMsg);
    }
  };

  const resendPhoneCode = async () => {
    if (!applicationId || resendCooldown > 0) return;

    setResendingCode(true);
    try {
      const response = await jobsAPI.resendPhoneCode(applicationId);
      if (response.data.success) {
        setResendCooldown(60); // 60 second cooldown
        setPhoneCode(''); // Clear input
      }
    } catch (error) {
      console.error('Resend code error:', error);
    } finally {
      setResendingCode(false);
    }
  };

  const handlePhoneCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPhoneCode(value);
    if (value.length === 6) {
      verifyPhone();
    }
  };

  const handlePhoneCodePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    // Extract only digits and limit to 6
    const digitsOnly = pastedText.replace(/\D/g, '').slice(0, 6);
    if (digitsOnly.length > 0) {
      setPhoneCode(digitsOnly);
      // Auto-verify if 6 digits pasted - pass code directly to avoid state update delay
      if (digitsOnly.length === 6) {
        verifyPhone(digitsOnly);
      }
    }
  };

  const resendVerificationEmail = async () => {
    if (!expiredTokenData || resendEmailCooldown > 0) return;

    setResendingEmail(true);
    try {
      const response = await jobsAPI.resendVerification(
        expiredTokenData.email,
        expiredTokenData.jobId,
        expiredTokenData.applicationId
      );
      
      if (response.data.success) {
        setResendEmailCooldown(120); // 2 minutes cooldown
        setMessage('A new verification email has been sent! Please check your inbox.');
        setStatus('verifying'); // Reset to verifying state
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to resend verification email. Please try again later.');
      if (error.response?.status === 429) {
        // Rate limited - set cooldown
        setResendEmailCooldown(120);
      }
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-card">
        {status === 'verifying' && (
          <>
            <div className="verification-spinner"></div>
            <h2>Verifying Your Email</h2>
            <p>Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="verification-icon success">✓</div>
            <h2>Email Verified</h2>
            
            {/* Optional Phone Verification Section */}
            {requiresPhoneVerification && phoneVerificationStatus !== 'success' && (
              <div className="phone-verification-section">
                <h3 className="phone-verification-title">
                  Verify Phone Number
                </h3>
                
                <p className="phone-verification-description">
                  Enter the 6-digit code sent to <strong>{phoneNumber || 'your phone'}</strong>
                </p>

                {phoneVerificationStatus === 'error' && (
                  <div className="phone-verification-error">
                    {phoneVerificationError || 'Invalid code. Please try again.'}
                  </div>
                )}

                {phoneVerificationStatus === 'verifying' && (
                  <div className="phone-verification-verifying">
                    Verifying...
                  </div>
                )}

                <div className="phone-verification-input-group">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    value={phoneCode}
                    onChange={handlePhoneCodeChange}
                    onPaste={handlePhoneCodePaste}
                    placeholder="000000"
                    className="phone-verification-input"
                    disabled={phoneVerificationStatus === 'verifying'}
                    autoFocus
                  />
                  <button
                    onClick={resendPhoneCode}
                    disabled={resendingCode || resendCooldown > 0}
                    className={`phone-verification-resend-btn ${resendCooldown > 0 ? 'disabled' : ''}`}
                  >
                    {resendCooldown > 0 ? `${resendCooldown}s` : 'Resend'}
                  </button>
                </div>
              </div>
            )}

            {phoneVerificationStatus === 'success' && (
              <div className="phone-verification-success">
                <div className="phone-verification-success-icon">✓</div>
                <p>Phone verified successfully</p>
              </div>
            )}

            {jobTitle && (
              <div className="verification-job-info">
                <p>Application for <strong>{jobTitle}</strong></p>
              </div>
            )}

            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Continue
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="verification-icon error">✗</div>
            <h2>Verification Failed</h2>
            <p>{message}</p>
            
            {expiredTokenData && (
              <div className="verification-resend-section">
                <button
                  onClick={resendVerificationEmail}
                  disabled={resendingEmail || resendEmailCooldown > 0}
                  className={`btn-primary ${resendEmailCooldown > 0 ? 'disabled' : ''}`}
                >
                  {resendingEmail 
                    ? 'Sending...' 
                    : resendEmailCooldown > 0 
                      ? `Resend (${resendEmailCooldown}s)` 
                      : 'Resend Email'}
                </button>
              </div>
            )}
            
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
